<template>
    <div class="container ol-page">
        <div class="ol-toolbar">
            <nav class="ol-tabs">
                <button
                    v-for="item in tabs"
                    :key="item.key"
                    type="button"
                    class="ol-tab"
                    :class="{ 'ol-tab--active': tab === item.key }"
                    @click="selectTab(item.key)"
                >
                    {{ item.label }}
                    <span class="ol-tab__count">{{ item.count }}</span>
                </button>
            </nav>

            <span v-if="loadedAt" class="ol-muted" :title="formatDateTime(loadedAt)">
                {{ __('Updated') }}: {{ fromNow(loadedAt) }}
            </span>
        </div>

        <div class="ol-subbar">
            <nav v-if="tab !== 'done'" class="ol-stages">
                <button
                    v-for="chip in stageChips"
                    :key="chip.key || 'all'"
                    type="button"
                    class="ol-chip"
                    :class="{ 'ol-chip--active': stage === chip.key }"
                    @click="selectStage(chip.key)"
                >
                    {{ chip.label }}
                </button>
            </nav>

            <p class="ol-hint">{{ hint }}</p>
        </div>

        <div class="matrix-panel">
            <div class="table-layout ol-table">
                <div class="table-head">
                    <div class="table-tr">
                        <div class="table-th">{{ __('Person') }}</div>
                        <div class="table-th">{{ __('Journey') }}</div>
                        <div class="table-th">{{ __('Note') }}</div>
                        <div class="table-th ol-cell-contact">{{ __('Contact') }}</div>
                    </div>
                </div>

                <div class="table-body">
                    <div v-if="loading && !leads.length" class="loader loader--chat loader--grey-background">
                        <i class="fa fa-spinner fa-spin fa-3x fa-fw" aria-hidden="true"></i>
                    </div>

                    <p v-else-if="!leads.length" class="ol-empty">
                        {{ tab === 'to_contact' ? __('Nothing to do right now.') : __('No leads.') }}
                    </p>

                    <template v-else>
                        <div
                            v-for="row in leads"
                            :key="row.id"
                            class="table-tr ol-row"
                            :class="{ 'ol-row--done': !row.is_open }"
                        >
                            <div class="table-td">
                                <div class="ol-person">
                                    <strong>{{ row.name || '—' }}</strong>
                                    <a v-if="row.email" :href="'mailto:' + row.email">{{ row.email }}</a>
                                    <a v-if="row.phone" :href="row.phone_link ? 'tel:' + row.phone_link : null">{{ row.phone }}</a>
                                    <span v-else class="ol-muted">{{ __('no phone') }}</span>
                                    <span v-if="personDetails(row)">{{ personDetails(row) }}</span>
                                    <span v-if="!row.mailing_enabled" class="ol-badge ol-badge--warn">{{ __('Unsubscribed') }}</span>
                                </div>
                            </div>

                            <div class="table-td">
                                <div class="ol-journey">
                                    <span class="ol-badge" :class="'ol-badge--' + row.stage">{{ stageLabelOf(row) }}</span>
                                    <span class="ol-meta" :title="formatDateTime(row.last_activity_at)">
                                        {{ __('Last activity') }}: {{ fromNow(row.last_activity_at) }}
                                    </span>
                                    <span v-if="row.needs_contact" class="ol-due">
                                        {{ __choice('{1} no activity for :days day|[2,*] no activity for :days days', quietDays(row), { days: quietDays(row) }) }}
                                    </span>
                                    <span v-if="row.closed_at" class="ol-meta" :title="formatDateTime(row.closed_at)">
                                        {{ __('Closed') }} {{ fromNow(row.closed_at) }}<template v-if="row.closed_by"> · {{ row.closed_by }}</template>
                                    </span>
                                </div>
                            </div>

                            <div class="table-td">
                                <follow-up-note
                                    :case-id="row.id"
                                    :note="row.admin_note"
                                    @save="saveNote"
                                ></follow-up-note>
                            </div>

                            <div class="table-td ol-cell-contact">
                                <div class="ol-contact">
                                    <template v-if="row.is_open">
                                        <div class="ol-buttons">
                                            <VButton
                                                :label="busyId === row.id ? __('saving…') : __('contacted')"
                                                :disabled="busyId !== null"
                                                size="sm"
                                                @onClick="markContacted(row)"
                                            />
                                            <VButton
                                                :label="__('rezept-service')"
                                                :disabled="busyId !== null"
                                                variant="secondary"
                                                size="sm"
                                                @onClick="close(row, 'rezept_service')"
                                            />
                                            <VButton
                                                :label="__('activated')"
                                                :disabled="busyId !== null"
                                                variant="secondary"
                                                size="sm"
                                                @onClick="close(row, 'activated')"
                                            />
                                            <VButton
                                                :label="__('cancel')"
                                                :disabled="busyId !== null"
                                                variant="ghost"
                                                color="danger"
                                                size="sm"
                                                @onClick="close(row, 'cancelled')"
                                            />
                                        </div>

                                        <span v-if="row.contacted_at" class="ol-meta" :title="formatDateTime(row.contacted_at)">
                                            {{ __('Contacted') }} {{ fromNow(row.contacted_at) }}<template v-if="row.contacted_by"> · {{ row.contacted_by }}</template>
                                        </span>
                                    </template>

                                    <template v-else-if="row.stage === 'closed'">
                                        <VButton
                                            :label="busyId === row.id ? __('saving…') : __('reopen')"
                                            :disabled="busyId !== null"
                                            variant="secondary"
                                            size="sm"
                                            @onClick="reopen(row)"
                                        />
                                    </template>

                                    <span v-else-if="row.contacted_at" class="ol-meta" :title="formatDateTime(row.contacted_at)">
                                        {{ __('Contacted') }} {{ fromNow(row.contacted_at) }}<template v-if="row.contacted_by"> · {{ row.contacted_by }}</template>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </template>

                    <VButton
                        v-if="hasMore"
                        :label="loading ? __('loading…') : __('Load more')"
                        :disabled="loading"
                        variant="secondary"
                        size="sm"
                        position="center"
                        @onClick="loadMore"
                    />
                </div>
            </div>
        </div>

        <flash-alert
            v-if="alert.show"
            :key="alert.key"
            :text="alert.text"
            :level="alert.level"
            position="top"
            @closeAlert="alert.show = false"
        ></flash-alert>
    </div>
</template>

<script>
import FollowUpNote from '@components/CoachAdmin/FollowUp/FollowUpNote.vue'
import followUpMixin from '@components/CoachAdmin/FollowUp/followUpMixin'

// the quiz stores the lower bound of the age band the person picked
const AGE_BANDS = {
    18: '18–29',
    30: '30–44',
    45: '45–59',
    60: '60+'
}

export default {
    components: {
        FollowUpNote
    },
    mixins: [followUpMixin],
    data() {
        return {
            tab: 'to_contact',
            stage: null,
            leads: [],
            page: 1,
            hasMore: false,
            counts: null,
            loading: true,
            loadedAt: null,
            busyId: null,
            alert: {
                show: false,
                text: '',
                level: 'success',
                key: 0
            }
        }
    },
    computed: {
        tabs() {
            return [
                { key: 'to_contact', label: this.__('To contact'), count: this.count('to_contact') },
                { key: 'open', label: this.__('All open'), count: this.count('open') },
                { key: 'done', label: this.__('Done'), count: this.count('done') }
            ]
        },
        stageChips() {
            return [
                { key: null, label: this.__('All') },
                { key: 'rezept_service', label: this.__('Rezept-Service') },
                { key: 'gomedicus', label: 'GoMedicus' },
                { key: 'stopped', label: this.__('Stopped early') }
            ]
        },
        hint() {
            return {
                to_contact: this.__('Quiet for too long at the step they stopped at and not contacted recently. Call them.'),
                open: this.__('Everyone still on the way, contacted or not. The longest without activity come first.'),
                done: this.__('Reached the Rezept-Service or the app, or closed by hand. The follow-up page takes over from here.')
            }[this.tab]
        }
    },
    mounted() {
        $(function() {
            $('.item-patient-leads').addClass('active')
        })

        this.fetch()
    },
    methods: {
        count(key) {
            return this.counts && this.counts[key] !== undefined ? this.counts[key] : ''
        },
        async fetch() {
            this.loading = true

            try {
                const response = await axios.get('/patient-leads/data', {
                    params: {
                        tab: this.tab,
                        stage: this.stage || undefined,
                        page: this.page
                    }
                })

                // the list comes in batches
                this.leads = this.page > 1
                    ? this.leads.concat(response.data.data.leads)
                    : response.data.data.leads
                this.hasMore = response.data.data.has_more

                this.applyMeta(response.data.meta)
                this.loadedAt = new Date().toISOString()
            } catch (error) {
                this.showAlert(this.errorMessage(error, this.__('Loading failed.')), 'danger')
            }

            this.loading = false
        },
        selectTab(key) {
            if (this.tab === key) {
                return
            }

            this.tab = key
            this.stage = null
            this.restart()
        },
        selectStage(key) {
            if (this.stage === key) {
                return
            }

            this.stage = key
            this.restart()
        },
        restart() {
            this.page = 1
            this.leads = []
            this.fetch()
        },
        loadMore() {
            this.page++
            this.fetch()
        },
        applyMeta(meta) {
            if (!meta) {
                return
            }

            this.counts = meta.counts || null
            this.updateNavCount(meta.counts)
        },
        // the badge in the main navigation is rendered by Blade; keep it in sync after an action
        updateNavCount(counts) {
            if (!counts) {
                return
            }

            document.querySelectorAll('[data-patient-leads-count]').forEach((element) => {
                const before = parseInt(element.textContent, 10) || 0

                element.textContent = counts.to_contact
                element.parentNode.style.display = counts.to_contact ? '' : 'none'

                // the "Leads" group badge is the sum of its items; move it by the same difference
                document.querySelectorAll('[data-leads-group-count]').forEach((group) => {
                    const total = Math.max(0, (parseInt(group.textContent, 10) || 0) - before + counts.to_contact)

                    group.textContent = total
                    group.parentNode.style.display = total ? '' : 'none'
                })
            })
        },
        async saveNote({ caseId, note, done }) {
            try {
                const response = await axios.put('/patient-leads/' + caseId + '/note', { note })

                this.replaceRow(response.data.data)
                this.showAlert(this.__('Note saved.'))
            } catch (error) {
                this.showAlert(this.errorMessage(error, this.__('Saving the note failed.')), 'danger')
            }

            if (typeof done === 'function') {
                done()
            }
        },
        async markContacted(row) {
            // a contacted lead leaves "to contact" until it is quiet for too long again
            await this.act(row, '/contacted', {}, this.__('Marked as contacted.'), this.tab === 'to_contact')
        },
        async close(row, reason) {
            if (reason === 'cancelled' && !window.confirm(this.__('Cancel this lead? It moves to done and can be reopened.'))) {
                return
            }

            const message = {
                rezept_service: this.__('Marked as Rezept-Service.'),
                activated: this.__('Marked as activated.'),
                cancelled: this.__('Lead cancelled.')
            }[reason]

            // a closed lead is done: it leaves the open tabs
            await this.act(row, '/close', { reason }, message, this.tab !== 'done')
        },
        async reopen(row) {
            // a reopened lead is open again: it leaves the done tab
            await this.act(row, '/reopen', {}, this.__('Lead reopened.'), this.tab === 'done')
        },
        async act(row, action, payload, message, removeRow) {
            this.busyId = row.id

            try {
                const response = await axios.post('/patient-leads/' + row.id + action, payload)

                if (removeRow) {
                    this.leads = this.leads.filter((current) => current.id !== row.id)
                } else {
                    this.replaceRow(response.data.data)
                }

                this.applyMeta(response.data.meta)
                this.showAlert(message)
            } catch (error) {
                this.showAlert(this.errorMessage(error, this.__('Saving failed.')), 'danger')
            }

            this.busyId = null
        },
        replaceRow(row) {
            const index = this.leads.findIndex((item) => item.id === row.id)

            if (index !== -1) {
                this.$set(this.leads, index, row)
            }
        },
        stageLabelOf(row) {
            if (row.stage === 'closed') {
                return {
                    rezept_service: this.__('Rezept-Service (manual)'),
                    activated: this.__('Activated (manual)'),
                    cancelled: this.__('Cancelled lead')
                }[row.closed_reason] || this.__('Closed')
            }

            return {
                rezept_service: this.__('Sent to Rezept-Service'),
                gomedicus: this.__('Sent to GoMedicus'),
                stopped: this.__('Stopped before the document question'),
                uploaded: this.__('Uploaded to Rezept-Service'),
                in_app: this.__('In the app')
            }[row.stage] || row.stage
        },
        personDetails(row) {
            return [
                row.insurance_company,
                row.dmp === null || row.dmp === undefined ? null : 'DMP: ' + this.yesNo(row.dmp),
                AGE_BANDS[row.age] || null
            ].filter(Boolean).join(' · ')
        },
        quietDays(row) {
            // rounded like fromNow() on the "last activity" line above, so both show the same number
            return Math.round(this.moment().diff(this.moment(row.last_activity_at), 'days', true))
        },
        errorMessage(error, fallback) {
            const data = error && error.response && error.response.data ? error.response.data : {}

            if (data.errors) {
                const first = Object.keys(data.errors)[0]

                if (first && data.errors[first] && data.errors[first][0]) {
                    return data.errors[first][0]
                }
            }

            return data.message || fallback
        },
        showAlert(text, level = 'success') {
            this.alert = {
                show: true,
                text,
                level,
                key: this.alert.key + 1
            }
        }
    }
}
</script>

<style scoped lang="less">
.ol-page {
    max-width: 1500px;
}

.ol-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin: 24px 0 12px;
}

.ol-subbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 16px;
}

.ol-tabs,
.ol-stages {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.ol-tab {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border: 1px solid #e4e2ee;
    border-radius: 20px;
    background: #fff;
    color: #676486;
    font-family: inherit;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        border-color: #5937b7;
        color: #5937b7;
    }

    &--active {
        background: #5937b7;
        border-color: #5937b7;
        color: #fff;

        &:hover {
            color: #fff;
        }

        .ol-tab__count {
            background: rgba(255, 255, 255, 0.25);
            color: #fff;
        }
    }

    &__count {
        padding: 0 7px;
        border-radius: 10px;
        background: #f3f3f6;
        color: #676486;
        font-size: 12px;
        font-weight: 700;
    }
}

.ol-chip {
    padding: 3px 12px;
    border: 1px solid #e4e2ee;
    border-radius: 14px;
    background: #fff;
    color: #676486;
    font-family: inherit;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        border-color: #5937b7;
        color: #5937b7;
    }

    &--active {
        border-color: #5937b7;
        background: fade(#5937b7, 10%);
        color: #5937b7;
        font-weight: 700;
    }
}

.ol-hint {
    margin: 0;
    color: #b3b1c2;
    font-size: 13px;
}

.ol-table {
    // Deliberately not using .table-patient: this table owns its layout (see the ExpertsConnect page).
    .table-tr {
        display: grid;
        grid-template-columns:
            minmax(220px, 1.2fr)
            minmax(210px, 1fr)
            minmax(320px, 2.4fr)
            300px;
        align-items: start;
        column-gap: 20px;
    }

    .table-head .table-tr {
        padding: 0 20px 10px;
    }

    .table-body .table-tr {
        margin-bottom: 12px;
        padding: 14px 20px;
        border: 1px solid rgba(102, 96, 121, 0.2);
        border-radius: 8px;
        background-color: #fff;
        box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.1);
    }

    .table-th {
        padding: 0;
        text-align: left;
        white-space: nowrap;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        color: #b3b1c2;
    }

    .table-td {
        min-width: 0;
        height: auto;
        padding: 0;
        text-align: left;
        background: none;
        font-size: 14px;
        overflow-wrap: break-word;
    }
}

.ol-row {
    transition: background-color 0.15s ease;

    &:hover {
        background-color: #f9f9fa;
    }

    &--done {
        opacity: 0.75;
    }
}

.ol-person,
.ol-journey,
.ol-contact {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 3px;

    strong {
        color: #2c2857;
    }

    span,
    a {
        color: #676486;
        font-size: 13px;
    }
}

.ol-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.4;
    background: #f3f3f6;
    color: #676486;

    &--rezept_service {
        background: fade(#fdbd05, 18%);
        color: darken(#fdbd05, 18%);
    }

    &--gomedicus {
        background: fade(#2ec9ff, 15%);
        color: darken(#2ec9ff, 20%);
    }

    &--stopped {
        background: fade(#ff9f1c, 16%);
        color: darken(#ff9f1c, 18%);
    }

    &--uploaded,
    &--in_app {
        background: fade(#00a671, 14%);
        color: #00a671;
    }

    &--closed {
        background: fade(#5937b7, 10%);
        color: #5937b7;
    }

    &--warn {
        background: fade(#ff3e57, 12%);
        color: #ff3e57;
    }
}

.ol-meta {
    color: #b3b1c2 !important;
    font-size: 11px !important;
}

.ol-due {
    color: #ff3e57 !important;
    font-size: 12px !important;
    font-weight: 700;
}

.ol-muted {
    color: #b3b1c2;
}

.ol-empty {
    padding: 40px 0;
    color: #b3b1c2;
    text-align: center;
}

.ol-cell-contact {
    text-align: right;
}

.ol-contact {
    align-items: flex-end;
    gap: 6px;
}

.ol-buttons {
    display: grid;
    grid-template-columns: repeat(2, max-content);
    justify-content: end;
    gap: 6px;

    .v-btn {
        justify-content: center;
    }
}
</style>
